import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  Chip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { Layout } from "../components/Layout";
import PurchaseOrderHeaderFields, {
  PurchaseOrderHeaderData,
} from "../components/PurchaseOrderHeaderFields";
import AddLineItemModal from "../components/modals/AddLineItemModal";
import apiClient from "../services/api";

interface LineItem {
  id?: number;
  chargeID: number;
  chargeCode: string;
  chargeDescription: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  patientID?: number;
  patientExternalID?: string;
  patientFirstname?: string;
  patientLastname?: string;
}

interface PurchaseOrderDetail {
  id: number;
  chargeID: number;
  chargeCode: string;
  chargeDescription: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  patientID?: number;
  patientExternalID?: string;
  patientFirstname?: string;
  patientLastname?: string;
}

const EditPurchaseOrderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);

  const [headerData, setHeaderData] = useState<PurchaseOrderHeaderData>({
    poNumber: "",
    clientID: 0,
    clientName: "",
    subClientId: undefined,
    subClientName: "",
    poDate: "",
    comment: "",
    statusID: 0,
    statusName: "",
    priorityID: 0,
    priorityName: "",
    signatureRequired: false,
    fromInventory: false,
  });

  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [originalFromInventory, setOriginalFromInventory] =
    useState<boolean>(false);

  const loadPurchaseOrder = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await apiClient.get(`/purchase-orders/${id}`);
      const data = response.data;

      setHeaderData({
        poNumber: data.poNumber,
        clientID: data.clientID,
        clientName: data.clientEntityName,
        subClientId: data.subClientId,
        subClientName: data.subClientName,
        poDate: data.poDate?.split("T")[0] || "",
        comment: data.comment || "",
        statusID: data.statusID,
        statusName: data.statusName,
        priorityID: data.priorityID,
        priorityName: data.priorityName,
        signatureRequired: data.signatureRequired,
        fromInventory: data.fromInventory,
      });

      // Store original fromInventory value
      setOriginalFromInventory(data.fromInventory || false);

      setLineItems(
        (data.details || []).map((d: PurchaseOrderDetail) => ({
          id: d.id,
          chargeID: d.chargeID,
          chargeCode: d.chargeCode,
          chargeDescription: d.chargeDescription,
          quantity: d.quantity,
          unitPrice: d.unitPrice,
          totalPrice: d.totalPrice,
          patientID: d.patientID,
          patientExternalID: d.patientExternalID,
          patientFirstname: d.patientFirstname,
          patientLastname: d.patientLastname,
        })),
      );
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load purchase order");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadPurchaseOrder();
  }, [loadPurchaseOrder]);

  const handleHeaderUpdate = async () => {
    setSaving(true);
    setError("");
    setSuccessMessage("");

    try {
      // Track if fromInventory changed
      const fromInventoryChanged =
        originalFromInventory !== headerData.fromInventory;

      // If fromInventory changed, update line item prices immediately in UI
      if (fromInventoryChanged) {
        const updatedLineItems = lineItems.map((item) => ({
          ...item,
          unitPrice: headerData.fromInventory ? 0 : item.unitPrice,
          totalPrice: headerData.fromInventory ? 0 : item.totalPrice,
        }));
        setLineItems(updatedLineItems);
      }

      await apiClient.patch(`/purchase-orders/${id}`, {
        poNumber: headerData.poNumber,
        poDate: headerData.poDate,
        clientID: headerData.clientID,
        clientName: headerData.clientName,
        subClientId: headerData.subClientId,
        subClientName: headerData.subClientName,
        comment: headerData.comment,
        statusID: headerData.statusID,
        priorityID: headerData.priorityID,
        signatureRequired: headerData.signatureRequired,
        fromInventory: headerData.fromInventory,
      });

      // If fromInventory changed, update all line item prices in backend
      if (fromInventoryChanged) {
        await apiClient.patch(`/purchase-orders/${id}/update-prices`, {
          fromInventory: headerData.fromInventory,
        });
        // Update the stored original value
        setOriginalFromInventory(headerData.fromInventory || false);
        // Reload the PO to get accurate prices from backend
        loadPurchaseOrder();
      }

      setSuccessMessage("Header updated successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update header");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLineItem = async (itemId: number) => {
    setSaving(true);
    setError("");

    try {
      await apiClient.delete(`/purchase-orders/${id}/details/${itemId}`);
      setLineItems(lineItems.filter((item) => item.id !== itemId));
      setSelectedItems(new Set());
      setSuccessMessage("Line item removed successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to remove line item");
    } finally {
      setSaving(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(lineItems.map((item) => item.id!).filter(Boolean));
      setSelectedItems(allIds);
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (itemId: number, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;

    setSaving(true);
    setError("");

    try {
      await Promise.all(
        Array.from(selectedItems).map((itemId) =>
          apiClient.delete(`/purchase-orders/${id}/details/${itemId}`),
        ),
      );
      setLineItems(lineItems.filter((item) => !selectedItems.has(item.id!)));
      setSelectedItems(new Set());
      setSuccessMessage(`${selectedItems.size} item(s) removed successfully`);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to remove items");
    } finally {
      setSaving(false);
    }
  };

  const calculateTotal = () => {
    return lineItems.reduce((sum, item) => sum + item.totalPrice, 0);
  };

  const getStatusColor = (statusID?: number) => {
    switch (statusID) {
      case 1:
        return "warning";
      case 2:
        return "success";
      case 3:
        return "error";
      case 4:
        return "info";
      case 5:
        return "default";
      case 6:
        return "warning";
      case 7:
        return "success";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priorityID?: number) => {
    switch (priorityID) {
      case 1:
        return "#c9892f"; // Low
      case 2:
        return "#569a45"; // Normal
      case 3:
        return "#c73e2e"; // High
      default:
        return "#569a45";
    }
  };

  // Calculate order summary - group by charge and sum quantities
  const getOrderSummary = () => {
    const summary: Record<
      number,
      {
        chargeCode: string;
        quantity: number;
        unitPrice: number;
        total: number;
      }
    > = {};

    lineItems.forEach((item) => {
      if (!summary[item.chargeID]) {
        summary[item.chargeID] = {
          chargeCode: item.chargeCode,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          total: item.totalPrice,
        };
      } else {
        summary[item.chargeID].quantity += item.quantity;
        summary[item.chargeID].total += item.totalPrice;
      }
    });

    return Object.values(summary);
  };

  // Group line items by patient
  const groupedLineItems = lineItems.reduce(
    (acc, item) => {
      const patientKey = item.patientID || 0;
      if (!acc[patientKey]) {
        acc[patientKey] = [];
      }
      acc[patientKey].push(item);
      return acc;
    },
    {} as Record<number, LineItem[]>,
  );

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box>
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/purchase-orders")}
          >
            Back to List
          </Button>
          <Typography variant='h4' sx={{ flexGrow: 1 }}>
            Edit Purchase Order - {headerData.poNumber}
          </Typography>
        </Box>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert
            severity='success'
            sx={{ mb: 2 }}
            onClose={() => setSuccessMessage("")}
          >
            {successMessage}
          </Alert>
        )}

        {/* Header Fields Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant='h6'>Purchase Order Details</Typography>
            <Button
              variant='contained'
              onClick={handleHeaderUpdate}
              disabled={saving}
            >
              {saving ? "Saving..." : "Update Header"}
            </Button>
          </Box>

          <PurchaseOrderHeaderFields
            data={headerData}
            onChange={setHeaderData}
            disabled={saving}
            showNextPoNumber={false}
          />
        </Paper>

        {/* Order Summary Section */}
        {lineItems.length > 0 && (
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant='h6' gutterBottom>
              Order Summary
            </Typography>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Items</TableCell>
                    <TableCell align='right'>Unit Price</TableCell>
                    <TableCell align='right'>Quantity</TableCell>
                    <TableCell align='right'>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getOrderSummary().map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.chargeCode}</TableCell>
                      <TableCell align='right'>
                        ${item.unitPrice?.toFixed(2) || "0.00"}
                      </TableCell>
                      <TableCell align='right'>{item.quantity}</TableCell>
                      <TableCell align='right'>
                        ${item.total?.toFixed(2) || "0.00"}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} align='right'>
                      <strong>Total:</strong>
                    </TableCell>
                    <TableCell align='right'>
                      <strong>${calculateTotal().toFixed(2)}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Line Items Section */}
        <Paper sx={{ p: 3, mt: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant='h6'>Line Items</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {selectedItems.size > 0 && (
                <Button
                  variant='outlined'
                  color='error'
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteSelected}
                  disabled={saving}
                >
                  Delete Selected ({selectedItems.size})
                </Button>
              )}
              <Button
                variant='contained'
                startIcon={<AddIcon />}
                onClick={() => setAddItemModalOpen(true)}
                disabled={saving || !headerData.clientID}
              >
                Add Item
              </Button>
            </Box>
          </Box>

          {/* Line Items Table */}
          {lineItems.length > 0 ? (
            <>
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell padding='checkbox'>
                        <Checkbox
                          checked={
                            selectedItems.size === lineItems.length &&
                            lineItems.length > 0
                          }
                          indeterminate={
                            selectedItems.size > 0 &&
                            selectedItems.size < lineItems.length
                          }
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          disabled={saving}
                        />
                      </TableCell>
                      <TableCell>Patient</TableCell>
                      <TableCell>Charge Code</TableCell>
                      <TableCell align='right'>Quantity</TableCell>
                      <TableCell align='right'>Unit Price</TableCell>
                      <TableCell align='right'>Total</TableCell>
                      <TableCell align='center'>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(groupedLineItems).map(
                      ([patientID, items]) => {
                        const firstItem = items[0];
                        const patientName =
                          firstItem.patientID && firstItem.patientID > 0
                            ? `${firstItem.patientLastname}, ${firstItem.patientFirstname}`
                            : "No Patient";

                        return (
                          <React.Fragment key={patientID}>
                            {items.map((item, index) => (
                              <TableRow key={item.id}>
                                <TableCell padding='checkbox'>
                                  <Checkbox
                                    checked={selectedItems.has(item.id!)}
                                    onChange={(e) =>
                                      handleSelectItem(
                                        item.id!,
                                        e.target.checked,
                                      )
                                    }
                                    disabled={saving}
                                  />
                                </TableCell>
                                <TableCell>
                                  {index === 0 && (
                                    <Typography
                                      variant='body2'
                                      fontWeight='bold'
                                    >
                                      {patientName}
                                      {firstItem.patientExternalID && (
                                        <Typography
                                          variant='caption'
                                          color='text.secondary'
                                          display='block'
                                        >
                                          ID: {firstItem.patientExternalID}
                                        </Typography>
                                      )}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>{item.chargeCode}</TableCell>
                                <TableCell align='right'>
                                  {item.quantity}
                                </TableCell>
                                <TableCell align='right'>
                                  ${item.unitPrice?.toFixed(2) || "0.00"}
                                </TableCell>
                                <TableCell align='right'>
                                  ${item.totalPrice?.toFixed(2) || "0.00"}
                                </TableCell>
                                <TableCell align='center'>
                                  <IconButton
                                    size='small'
                                    color='error'
                                    onClick={() =>
                                      handleRemoveLineItem(item.id!)
                                    }
                                    disabled={saving}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        );
                      },
                    )}
                    <TableRow>
                      <TableCell colSpan={5} align='right'>
                        <strong>Total:</strong>
                      </TableCell>
                      <TableCell align='right'>
                        <strong>${calculateTotal().toFixed(2)}</strong>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Alert severity='info'>
              No line items yet. Click "Add Item" above to get started.
            </Alert>
          )}
        </Paper>

        <AddLineItemModal
          open={addItemModalOpen}
          purchaseOrderID={Number(id)}
          clientID={headerData.clientID}
          fromInventory={headerData.fromInventory || false}
          onClose={() => setAddItemModalOpen(false)}
          onSuccess={() => {
            setAddItemModalOpen(false);
            loadPurchaseOrder();
          }}
        />
      </Box>
    </Layout>
  );
};

export default EditPurchaseOrderPage;
